import { Injectable, inject } from "@angular/core";
import { AlertController, NavController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import {
  Observable,
  Subscription,
  defer,
  firstValueFrom,
  from,
  of,
} from "rxjs";
import {
  catchError,
  map,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from "rxjs/operators";

import {
  CountryCodeEnum,
  CountryInfo,
} from "src/app/shared/models/country.model";
import { GetUserByIdResponse } from "src/app/shared/models/user.model";
import { AuthService } from "src/app/shared/services/auth.service";
import { CountrySelectionHandoffService } from "src/app/shared/services/country-selection-handoff.service";
import { LoggerService } from "src/app/shared/services/logger.service";
import { UserService } from "src/app/shared/services/user.service";
import { ZABetaLockExperimentKeyService } from "./ZABetaLock.service";

export interface CountryResolution {
  country: CountryCodeEnum;
  userData: GetUserByIdResponse | null;
}

/** Owns ZA startup decisions and current-session live enforcement. */
@Injectable({ providedIn: "root" })
export class ZABetaLockCoordinatorService {
  private readonly zaBetaLockService = inject(ZABetaLockExperimentKeyService);
  private readonly userService = inject(UserService);
  private readonly countrySelectionHandoffService = inject(
    CountrySelectionHandoffService
  );
  private readonly authService = inject(AuthService);
  private readonly alertController = inject(AlertController);
  private readonly navController = inject(NavController);
  private readonly translateService = inject(TranslateService);
  private readonly loggerService = inject(LoggerService);
  private liveEnforcementSubscription: Subscription | null = null;
  private activeLockSelection: Promise<CountryInfo> | null = null;
  private blockedAccessAlert: HTMLIonAlertElement | null = null;
  private blockedAccessPresentation: Promise<void> | null = null;
  private blockedAccessLogout: Promise<void> | null = null;
  private clearBlockedAccessAfterLogout = false;
  private authenticatedZAAccessBlocked = false;
  private blockedAccessOwnerUid: string | null = null;
  private blockedAccessResetSubscription: Subscription | null = null;
  private blockedAccessGeneration = 0;

  guardCountryResolution$(
    result: CountryResolution
  ): Observable<CountryResolution | null> {
    const user = result.userData?.user;
    const userCountry = user?.country;
    const hasSupportedUserCountry =
      userCountry && Object.values(CountryCodeEnum).includes(userCountry);
    const accessCountry = hasSupportedUserCountry
      ? userCountry
      : result.country;

    if (accessCountry !== CountryCodeEnum.za) {
      return of(result);
    }

    if (!this.zaBetaLockService.isActive(user, CountryCodeEnum.za)) {
      return of(result);
    }

    if (this.authenticatedZAAccessBlocked) {
      return of(null);
    }

    if (user?.userId && userCountry === CountryCodeEnum.za) {
      return this.enforceAuthenticatedLock$(user.userId).pipe(map(() => null));
    }

    return this.enforceActiveLock$(user?.userId || "").pipe(map(() => null));
  }

  startLiveEnforcement(): void {
    if (this.liveEnforcementSubscription !== null) {
      return;
    }

    this.liveEnforcementSubscription = this.zaBetaLockService
      .liveValue$()
      .pipe(
        withLatestFrom(
          this.userService.userCountryWatch$,
          this.countrySelectionHandoffService.pendingCountryWatch$
        ),
        tap(([lockActive, , handoffCountry]) => {
          if (!lockActive) {
            if (handoffCountry === CountryCodeEnum.za) {
              this.countrySelectionHandoffService.allow(CountryCodeEnum.za);
            }

            if (this.blockedAccessLogout === null) {
              this.clearAuthenticatedBlock();
            } else {
              this.clearBlockedAccessAfterLogout = true;
            }
          } else {
            this.clearBlockedAccessAfterLogout = false;
          }

          if (lockActive && handoffCountry === CountryCodeEnum.za) {
            this.countrySelectionHandoffService.block(CountryCodeEnum.za);
          }
        }),
        switchMap(([lockActive, countryInfo, handoffCountry]) => {
          if (!lockActive || this.authenticatedZAAccessBlocked) {
            return of(null);
          }

          return this.resolveLiveEnforcement$(
            countryInfo?.country === CountryCodeEnum.za ||
              handoffCountry === CountryCodeEnum.za
          );
        })
      )
      .subscribe({
        error: (error) => this.loggerService.logError(error),
      });
  }

  stopLiveEnforcement(): void {
    this.liveEnforcementSubscription?.unsubscribe();
    this.liveEnforcementSubscription = null;
    this.blockedAccessResetSubscription?.unsubscribe();
    this.blockedAccessResetSubscription = null;
  }

  /** Shows one terminal block for concurrent authenticated ZA paths. */
  enforceAuthenticatedLock$(
    userId: string,
    expectedFirebaseUid?: string
  ): Observable<void> {
    return defer(() => {
      const ownerUid = expectedFirebaseUid ?? userId;
      if (
        this.blockedAccessOwnerUid !== null &&
        this.blockedAccessOwnerUid !== ownerUid
      ) {
        this.clearAuthenticatedBlock();
      }

      this.authenticatedZAAccessBlocked = true;
      this.blockedAccessOwnerUid = ownerUid;
      this.resetBlockedAccessOnNextLogin(ownerUid);
      if (this.blockedAccessPresentation === null) {
        const generation = ++this.blockedAccessGeneration;
        this.blockedAccessPresentation = this.presentBlockedAccess(
          ownerUid,
          generation
        ).catch((error) => {
          this.loggerService.logError(error);
          this.clearAuthenticatedBlock();
        });
      }

      return from(this.blockedAccessPresentation);
    });
  }

  /** Shares the ZA-blocking selector across concurrent unknown-country paths. */
  enforceActiveLock$(userId: string): Observable<CountryInfo> {
    return defer(() => {
      if (this.activeLockSelection !== null) {
        return from(this.activeLockSelection);
      }

      if (this.userService.isCountrySelectionOpen()) {
        return of(null);
      }

      const selection = this.userService.handleUnknownCountry(userId, true);
      this.activeLockSelection = selection;

      return from(
        selection.finally(() => {
          if (this.activeLockSelection === selection) {
            this.activeLockSelection = null;
          }
        })
      );
    });
  }

  private resolveLiveEnforcement$(
    shouldSelectCountry: boolean
  ): Observable<CountryInfo | void> {
    return this.authService.getFireAuthUser().pipe(
      take(1),
      switchMap((firebaseUser) => {
        if (!firebaseUser?.uid) {
          return shouldSelectCountry ? this.enforceActiveLock$("") : of(null);
        }

        return this.userService
          .getUserById({ hideLoading: true, hideError: true })
          .pipe(
            take(1),
            switchMap((userData) =>
              from(this.authService.getFirebaseCurrentUser()).pipe(
                switchMap((currentFirebaseUser) => {
                  if (currentFirebaseUser?.uid !== firebaseUser.uid) {
                    return of(null);
                  }

                  const user = userData?.user;
                  if (!user?.userId) {
                    return of(null);
                  }

                  if (user.country === CountryCodeEnum.za) {
                    return this.enforceAuthenticatedLock$(
                      user.userId,
                      firebaseUser.uid
                    );
                  }

                  const hasSupportedCountry =
                    user.country &&
                    Object.values(CountryCodeEnum).includes(user.country);
                  return !hasSupportedCountry && shouldSelectCountry
                    ? this.enforceActiveLock$(user.userId)
                    : of(null);
                })
              )
            )
          );
      }),
      catchError((error) => {
        this.loggerService.logError(error);
        return of(null);
      })
    );
  }

  private async presentBlockedAccess(
    ownerUid: string,
    generation: number
  ): Promise<void> {
    const firebaseUser = await this.authService.getFirebaseCurrentUser();
    if (generation !== this.blockedAccessGeneration) {
      return;
    }

    if (!ownerUid || firebaseUser?.uid !== ownerUid) {
      this.clearAuthenticatedBlock();
      return;
    }

    if (this.blockedAccessAlert !== null) {
      return;
    }

    const alert = await this.alertController.create({
      header: this.translateService.instant("FINANCING.SERVICE_NOT_AVAILABLE"),
      backdropDismiss: false,
      buttons: [
        {
          text: this.translateService.instant("LOGOUT"),
          handler: () => {
            this.startBlockedAccessLogout(alert, ownerUid);
            return false;
          },
        },
      ],
    });
    if (generation !== this.blockedAccessGeneration) {
      return;
    }

    this.blockedAccessAlert = alert;
    await alert.present();
  }

  private startBlockedAccessLogout(
    alert: HTMLIonAlertElement,
    expectedFirebaseUid: string
  ): void {
    if (this.blockedAccessLogout !== null) {
      return;
    }

    const logout = this.logoutFromBlockedAccess(
      alert,
      expectedFirebaseUid
    ).finally(() => {
      if (this.blockedAccessLogout === logout) {
        this.blockedAccessLogout = null;
        if (this.clearBlockedAccessAfterLogout) {
          this.clearAuthenticatedBlock();
        }
      }
    });
    this.blockedAccessLogout = logout;
    void logout;
  }

  private async logoutFromBlockedAccess(
    alert: HTMLIonAlertElement,
    expectedFirebaseUid: string
  ): Promise<void> {
    try {
      const currentFirebaseUser =
        await this.authService.getFirebaseCurrentUser();
      if (
        currentFirebaseUser?.uid &&
        currentFirebaseUser.uid !== expectedFirebaseUid
      ) {
        this.clearAuthenticatedBlock();
        return;
      }

      // Keep the terminal alert in place until all canonical cleanup finishes.
      // This avoids exposing a replacement login to late logout side effects.
      if (currentFirebaseUser?.uid) {
        await firstValueFrom(this.authService.logout());
      }
      const didNavigate = await this.navController.navigateRoot("/login");
      if (!didNavigate) {
        throw new Error("ZA blocked-access navigation failed");
      }

      await alert.dismiss();
      if (this.blockedAccessAlert === alert) {
        this.blockedAccessAlert = null;
        this.blockedAccessPresentation = null;
      }
    } catch (error) {
      this.loggerService.logError(error);
    }
  }

  private resetBlockedAccessOnNextLogin(ownerUid: string): void {
    if (this.blockedAccessResetSubscription !== null) {
      return;
    }

    let observedSignOut = false;
    this.blockedAccessResetSubscription = this.authService
      .getFireAuthUser()
      .subscribe({
        next: (firebaseUser) => {
          if (!firebaseUser?.uid) {
            observedSignOut = true;
            return;
          }

          if (observedSignOut || firebaseUser.uid !== ownerUid) {
            this.clearAuthenticatedBlock();
          }
        },
        error: (error) => this.loggerService.logError(error),
      });
  }

  private clearAuthenticatedBlock(): void {
    this.blockedAccessGeneration += 1;
    this.clearBlockedAccessAfterLogout = false;
    this.authenticatedZAAccessBlocked = false;
    this.blockedAccessOwnerUid = null;
    this.blockedAccessResetSubscription?.unsubscribe();
    this.blockedAccessResetSubscription = null;
    this.blockedAccessPresentation = null;
    const alert = this.blockedAccessAlert;
    this.blockedAccessAlert = null;
    if (alert) {
      void alert.dismiss().catch((error) => this.loggerService.logError(error));
    }
  }
}
